import React, { ChangeEvent, useMemo, useState } from 'react'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormFileField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CreatePromoMaterial } from 'api/ClientArea/CreatePromoMaterials'
import { PreviewPromoMaterial } from 'api/ClientArea/PreviewPromoMaterials'
import { PromoMaterialDto } from 'api/ClientArea/PromoMaterialDto'
import { UpdatePromoMaterial } from 'api/ClientArea/UpdatePromoMaterial'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { convertDateToUTC, createDateAsUTC, getOneYearFromCurrentDate } from 'utils/date'
import { toBase64 } from 'utils/file.helpers'
import { FormSubmitValues } from 'utils/formValidation'

import { NameDto } from '../../../api/Common/Types'
import { LoadingOverlay } from '../../../components/LoadingOverlay/LoadingOverlay'
import { UploadTooltip } from './components/UploadTooltip'

export interface PromoMaterialFormValues {
  name?: string
  image?: string
  fileImage?: string
  promoMaterialCategoryId?: number
  promoMaterialTypeId?: number
  languageId?: string
  languageName?: string
  enabled?: boolean
  fileSize?: string
  endDateTime?: string
  fileTypeError?: string
}

const ALLOWED_STATIC_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/bmp']

const PromoMaterialFormUI: React.FC<FormikProps<PromoMaterialFormValues> & OuterProps> = (
  props
) => {
  const {
    caLanguages,
    values,
    setFieldValue,
    isNew = false,
    promoMaterialPreview,
    isEdit,
    setEdit,
    isSubmitting,
    sizes,
  } = props
  const [isPreviewValidated, setPreviewValidated] = useState<boolean>(!isNew)
  const [isLoading, setLoading] = useState<boolean>(false)
  const languageOptions = useMemo(
    () => (caLanguages || []).map((language) => ({ id: language.id, name: language.name })),
    [caLanguages]
  )

  const handlePromoMaterialValidate = async () => {
    if (isFormDataFilled(values)) {
      try {
        setLoading(true)

        const newData = dataToSubmit(props)(values)

        if (newData && props?.onValidate) {
          await props.onValidate([newData])
          setPreviewValidated(true)
        }
      } catch (e) {
        console.error(e)
        setPreviewValidated(false)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleUploadImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files || []

    if (files.length === 0) {
      console.error('No file selected.')
      await setFieldValue('fileImage', undefined)
      await setFieldValue('fileSize', undefined)
      await setFieldValue('fileTypeError', undefined)

      return
    }

    if (!ALLOWED_STATIC_IMAGE_TYPES.includes(files[0].type)) {
      console.error('Invalid file type:', files[0].type)
      await setFieldValue('fileImage', undefined)
      await setFieldValue('fileSize', undefined)
      await setFieldValue('fileTypeError', 'Promo material file format is invalid')

      return
    } else {
      await setFieldValue('fileTypeError', undefined)
    }

    try {
      const [file] = files
      const imageElement = await handleFileUpload(file)

      if (imageElement) {
        setFieldValue('fileSize', `${imageElement.width}x${imageElement.height}`)
        setFieldValue('fileImage', imageElement.src)
      }
    } catch (error) {
      if (error instanceof Error && error.message) {
        console.error('Error reading file:', error.message)
      }
    }
  }

  const isValid = (): boolean => !!values.fileImage && props.isValid

  return (
    <Form>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add new Promo Material' : 'Edit Promo Material'}</DialogTitle>
        </DialogHeader>
        <FormField name='name' label='Name' readOnly={!isEdit} value={values.name} />

        {!isNew && (
          <FormReadonlyField name='image' label='Current Image Path' value={values.image} />
        )}

        {isNew && isEdit && (
          <UploadTooltip sizes={sizes}>
            <FormFileField
              accept='image/jpeg'
              onChange={handleUploadImage}
              name='fileImage'
              label='Image'
            />
          </UploadTooltip>
        )}

        {isPreviewValidated && promoMaterialPreview && (
          <FormReadonlyField
            name='promoMaterialPreview.name'
            label='Size ID'
            value={promoMaterialPreview.size?.name}
          />
        )}

        {isPreviewValidated && promoMaterialPreview && (
          <FormReadonlyField
            name='promoMaterialType.name'
            label='Promo Material Type'
            value={promoMaterialPreview.contentType?.name}
          />
        )}

        <FormSelectField
          name='languageName'
          label='Language'
          value={values.languageId}
          options={languageOptions}
          readOnly={!isEdit}
          showSearch
          allowClear
          onChange={(value) => setFieldValue('languageId', value)}
        />

        <FormDateField name='endDateTime' label='End Date' readOnly={!isEdit} />

        <FormCheckboxField name='enabled' label='Active' readOnly={!isEdit} />
        <DialogFooter>
          <ActionButtons
            {...props}
            isEdit={isEdit}
            setEdit={setEdit}
            isPreviewValidated={isPreviewValidated}
            setPreviewValidated={setPreviewValidated}
            onPromoMaterialValidate={handlePromoMaterialValidate}
            isValid={isValid()}
          />
        </DialogFooter>
        <LoadingOverlay isLoading={isSubmitting || isLoading} />
      </DialogContent>
    </Form>
  )
}

export type CreatePromoMaterialValues = Omit<CreatePromoMaterial, 'id'>
export type UpdatePromoMaterialValues = UpdatePromoMaterial & { id: string }

export const dataToSubmit =
  (props: OuterProps) =>
  (
    values: Required<PromoMaterialFormValues>
  ): CreatePromoMaterialValues | UpdatePromoMaterialValues => {
    const { promoMaterial } = props

    if (promoMaterial?.id) {
      return {
        id: promoMaterial.id,
        name: values.name || promoMaterial.name || null,
        languageId: values?.languageId || promoMaterial.language.id,
        enabled: values.enabled ?? promoMaterial.enabled ?? false,
        endDateTime: values?.endDateTime
          ? createDateAsUTC(values.endDateTime, false, true).toISOString()
          : null,
        promoAsset: values.fileImage,
      }
    }

    return {
      name: values.name,
      languageId: values.languageId,
      enabled: values?.enabled ?? false,
      endDateTime: values?.endDateTime
        ? createDateAsUTC(values.endDateTime, false, true).toISOString()
        : null,
      promoAsset: values.fileImage,
    }
  }

interface OuterProps {
  caLanguages: NameDto<string>[]
  promoMaterial?: PromoMaterialDto
  sizes: NameDto[]
  promoMaterialPreview?: PreviewPromoMaterial
  onSubmit(
    values: FormSubmitValues<CreatePromoMaterialValues | UpdatePromoMaterialValues>
  ): Promise<void>
  onValidate?(
    values: FormSubmitValues<(CreatePromoMaterialValues | UpdatePromoMaterialValues)[]>
  ): Promise<PreviewPromoMaterial[] | undefined>
  isNew?: boolean
  isEdit: boolean
  setEdit(value: boolean): void
}

export const PromoMaterialForm = withFormik<OuterProps, PromoMaterialFormValues>({
  mapPropsToValues: (props) => {
    const { promoMaterial, isNew, sizes } = props
    let endDateTime

    if (isNew) {
      endDateTime = getOneYearFromCurrentDate()
    } else if (promoMaterial?.endDateTime) {
      endDateTime = convertDateToUTC(promoMaterial.endDateTime).toISOString()
    }

    return {
      name: promoMaterial?.name ?? undefined,
      image: promoMaterial?.imagePath ?? undefined,
      fileImage: promoMaterial?.imagePath ?? undefined,
      languageId: promoMaterial?.language?.id ?? undefined,
      languageName: promoMaterial?.language?.name ?? undefined,
      endDateTime,
      enabled: promoMaterial?.enabled ?? true,
      fileSize: undefined,
      fileTypeError: undefined,
      sizes,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      if (isFormDataFilled(values)) {
        const data = dataToSubmit(props)(values)
        await props.onSubmit(data)
      }
    } finally {
      setSubmitting(false)
    }
  },
  validate: async (values) => {
    const errors: FormikErrors<PromoMaterialFormValues> = {}

    if (!values.name) {
      errors.name = 'Required'
    }

    if (!values.fileImage) {
      errors.fileImage = values.fileTypeError || 'Required'
    }

    if (!values.languageId) {
      errors.languageId = 'Required'
    }

    return errors
  },
  enableReinitialize: false,
  validateOnChange: true,
})(PromoMaterialFormUI)

interface ActionProps extends FormikProps<PromoMaterialFormValues>, OuterProps {
  isEdit: boolean
  setEdit(value: boolean): void
  isPreviewValidated: boolean
  setPreviewValidated(value: boolean): void
  onPromoMaterialValidate(): void
}

const ActionButtons = (props: ActionProps) => {
  const { isNew } = props

  if (isNew) {
    return <ActionNewButtons {...props} />
  }

  return <ActionEditButtons {...props} />
}

const ActionNewButtons = (props: ActionProps) => {
  const { isValid, isSubmitting, handleSubmit, isPreviewValidated, onPromoMaterialValidate } = props

  const handleSave = () => {
    handleSubmit()
  }

  if (isPreviewValidated) {
    return (
      <ButtonGroup>
        <Button
          type='button'
          disabled={!isValid}
          key='button'
          onClick={handleSave}
          data-test='save-button'
        >
          Save
        </Button>
      </ButtonGroup>
    )
  }

  return (
    <ButtonGroup>
      <Button
        type='button'
        disabled={!isValid || isSubmitting}
        key='button'
        onClick={onPromoMaterialValidate}
        data-test='validate-button'
      >
        Validate
      </Button>
    </ButtonGroup>
  )
}

const ActionEditButtons = (props: ActionProps) => {
  const { isEdit, setEdit, isValid, isSubmitting, resetForm, submitForm } = props

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleSave = () => {
    setEdit(false)
    submitForm()
  }

  return (
    <ButtonGroup>
      {!isEdit ? (
        <Button onClick={() => setEdit(true)} data-test='edit-button'>
          Edit
        </Button>
      ) : (
        <React.Fragment>
          <Button
            variant='outline'
            className='is-danger'
            onClick={handleCancel}
            data-test='cancel-button'
          >
            Cancel
          </Button>
          <Button
            disabled={!isValid || isSubmitting}
            key='button'
            type='button'
            onClick={handleSave}
            data-test='save-button'
          >
            Save
          </Button>
        </React.Fragment>
      )}
    </ButtonGroup>
  )
}

const isFormDataFilled = (
  values: PromoMaterialFormValues
): values is Required<PromoMaterialFormValues> => {
  const requiredFields: (keyof PromoMaterialFormValues)[] = ['name', 'fileImage']
  return requiredFields.every((field) => values[field] !== undefined)
}

const handleFileUpload = async (file: File): Promise<HTMLImageElement> => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    const base64 = await toBase64(file)
    if (base64) {
      const image = await getImage(base64)
      resolve(image)
    } else {
      reject()
    }
  })
}

const getImage = async (base64?: string | ArrayBuffer): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const newBase64 = typeof base64 === 'string' ? base64 : ''

    img.onload = function () {
      img.src = newBase64 as string
      resolve(this as HTMLImageElement)
    }

    img.onerror = function (error) {
      reject(error)
    }

    img.src = newBase64 as string
  })
}
